<template>
  <div class="video-analytics">
    <Sidebar />
    <div class="content-wrapper">
      <div class="header">
        <div class="title-section">
          <h1>Video Analytics</h1>
          <p class="subtitle">Track and analyze your video performance metrics</p>
        </div>
        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ error }}
        </div>
      </div>
      
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading analytics data...</p>
      </div>

      <template v-else>
        <div class="time-period-selector">
          <button 
            v-for="period in periods" 
            :key="period"
            :class="['period-btn', { active: selectedPeriod === period }]"
            @click="selectedPeriod = period"
          >
            {{ period }}
          </button>
        </div>

        <div v-if="currentPeriodData" class="analytics-container">
          <QuickStats :performance-data="performanceData" />
          <AnalyticsGrid
            :current-period-data="currentPeriodData"
            :impressions-plays-data="impressionsPlaysData"
            :engagement-data="engagementData"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import QuickStats from '@/components/analytics/QuickStats.vue';
import AnalyticsGrid from '@/components/analytics/AnalyticsGrid.vue';

export default {
  name: 'VideoAnalytics',
  components: { Sidebar, QuickStats, AnalyticsGrid },

  setup() {
    const analytics = ref(null);
    const selectedPeriod = ref('monthly');
    const periods = ['daily', 'weekly', 'biweekly', 'monthly'];
    const loading = ref(true);
    const error = ref(null);
    const performanceData = ref(null);
    const impressionsData = ref(null);

    const fetchAnalytics = async () => {
      try {
        loading.value = true;
        error.value = null;
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token found');

        const response = await axios.get('https://api.kewaar.com/api/admin/video-analytics', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch analytics data');
        }

        analytics.value = response.data.data;
      } catch (err) {
        error.value = err.message || 'An error occurred while fetching analytics data';
        console.error('Analytics fetch error:', err);
      } finally {
        loading.value = false;
      }
    };

    const fetchImpressions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token found');

        const response = await axios.get('https://api.kewaar.com/api/admin/video-analytics/impressions', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch impressions data');
        }

        impressionsData.value = response.data.data.impressions;
      } catch (err) {
        console.error('Impressions fetch error:', err);
      }
    };

    const fetchPerformanceData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token found');

        const response = await axios.get('https://api.kewaar.com/api/admin/video-analytics/performance', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch performance data');
        }

        performanceData.value = response.data.data;
      } catch (err) {
        console.error('Performance data fetch error:', err);
      }
    };

    const currentPeriodData = computed(() => {
      return analytics.value ? analytics.value[selectedPeriod.value] : null;
    });

    const impressionsPlaysData = computed(() => ({
      labels: ['Metrics'],
      datasets: [
        {
          label: 'Impressions',
          data: [impressionsData.value?.[selectedPeriod.value] || 0],
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Plays',
          data: [currentPeriodData.value?.plays || 0],
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    }));

    const engagementData = computed(() => ({
      labels: ['Engaged', 'Not Engaged'],
      datasets: [{
        data: [
          currentPeriodData.value?.engagement_rate || 0,
          100 - (currentPeriodData.value?.engagement_rate || 0)
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(201, 203, 207, 0.6)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(201, 203, 207, 1)'
        ],
        borderWidth: 1
      }]
    }));

    const formatTotalWatchTime = (seconds) => {
      if (!seconds) return '0:00';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      
      let result = '';
      if (hours > 0) result += `${hours}h `;
      if (minutes > 0) result += `${minutes}m `;
      result += `${remainingSeconds}s`;
      return result.trim();
    };

    onMounted(() => {
      fetchAnalytics();
      fetchPerformanceData();
      fetchImpressions();
    });

    return {
      selectedPeriod,
      periods,
      loading,
      error,
      currentPeriodData,
      impressionsPlaysData,
      engagementData,
      performanceData,
      formatTotalWatchTime,
      impressionsData,
    };
  }
};
</script>

<style scoped>
.video-analytics {
  min-height: 100vh;
  background-color: #f8fafc;
  display: flex;
  position: relative;
  overflow: hidden;
}

.content-wrapper {
  flex: 1;
  padding: 2rem;
  margin-left: 280px;
  max-width: calc(100% - 280px);
  height: 100vh;
  overflow-y: auto;
  position: relative;
}

.header {
  margin-bottom: 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.title-section {
  margin-bottom: 1rem;
}

.title-section h1 {
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.subtitle {
  margin: 0.5rem 0 0;
  color: #64748b;
  font-size: 1.1rem;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  padding: 1rem;
  background: #fef2f2;
  border-left: 4px solid #dc2626;
  border-radius: 6px;
  margin-top: 1rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.time-period-selector {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  background: white;
  padding: 0.75rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.period-btn {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.period-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.period-btn.active {
  background: #2563eb;
  color: white;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.analytics-container {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .content-wrapper {
    margin-left: 0;
    max-width: 100%;
    padding: 1rem;
  }
  
  .title-section h1 {
    font-size: 2rem;
  }
  
  .time-period-selector {
    flex-wrap: wrap;
  }
}
</style> 